import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Loader2, PlusCircle, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import {
  type AppointmentStatus,
  type AppointmentStatusFinalType,
  useAppointmentStatuses,
  useUpdateClinicAppointmentStatuses,
} from "@/hooks/useAppointmentStatuses";
import { useProfile } from "@/hooks/useProfile";
import { toast } from "sonner";
import { getStatusColor } from "@/lib/utils/clinic-appointment-status";

type DraftStatus = {
  client_id: string;
  name?: string;
  display_name: string;
  is_active: boolean;
  was_active: boolean;
  sort_order: number;
  color: string;
  final_type: AppointmentStatusFinalType;
  replacement_name: string | null;
  is_system: boolean;
  is_new: boolean;
};

const FINAL_TYPE_OPTIONS: AppointmentStatusFinalType[] = ["normal", "finalizado", "cancelado"];

const createDraftId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createDraftStatusName = () =>
  `custom_appointment_${createDraftId().replace(/[^a-zA-Z0-9]+/g, "").slice(0, 16).toLowerCase()}`;

function mapStatusesToDraft(statuses: AppointmentStatus[]): DraftStatus[] {
  return [...statuses]
    .sort((left, right) => left.sort_order - right.sort_order || left.name.localeCompare(right.name))
    .map((status) => ({
      client_id: createDraftId(),
      name: status.name,
      display_name: status.display_name,
      is_active: status.is_active,
      was_active: status.is_active,
      sort_order: status.sort_order,
      color: status.color,
      final_type: status.final_type,
      replacement_name: null,
      is_system: status.is_system,
      is_new: false,
    }));
}

function StatusScopeSection({
  title,
  description,
  statuses,
  busy,
  disabled,
  onChange,
  onAdd,
  onRemove,
  onSave,
}: {
  title: string;
  description: string;
  statuses: DraftStatus[];
  busy: boolean;
  disabled: boolean;
  onChange: (clientId: string, next: Partial<DraftStatus>) => void;
  onAdd: () => void;
  onRemove: (clientId: string) => void;
  onSave: () => void;
}) {
  const { t } = useTranslation("settings");

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="space-y-1">
        <p className="text-base font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="space-y-3">
        {statuses.map((status) => {
          const activeReplacementOptions = statuses.filter(
            (candidate) => candidate.client_id !== status.client_id && candidate.is_active,
          );

          return (
            <div key={status.client_id} className="space-y-3 rounded-md border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: status.color }} />
                  <span className="text-sm font-medium">{status.display_name || t("appointmentStatusesNewStatus")}</span>
                  {status.is_system ? <Badge variant="outline">{t("appointmentStatusesSystemBadge")}</Badge> : null}
                  {status.is_new ? <Badge variant="secondary">{t("appointmentStatusesNewBadge")}</Badge> : null}
                </div>
                {status.is_new ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={busy || disabled}
                    onClick={() => onRemove(status.client_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>

              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_180px_140px]">
                <div className="space-y-1">
                  <Label htmlFor={`${status.client_id}-display-name`}>{t("appointmentStatusesDisplayNameLabel")}</Label>
                  <Input
                    id={`${status.client_id}-display-name`}
                    value={status.display_name}
                    disabled={disabled || busy}
                    onChange={(event) => onChange(status.client_id, { display_name: event.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("appointmentStatusesSystemKey")}: <code>{status.name ?? t("appointmentStatusesGeneratedKey")}</code>
                  </p>
                </div>

                <div className="space-y-1">
                  <Label htmlFor={`${status.client_id}-final-type`}>{t("appointmentStatusesFinalTypeLabel")}</Label>
                  <Select
                    value={status.final_type}
                    onValueChange={(value) => onChange(status.client_id, { final_type: value as AppointmentStatusFinalType })}
                    disabled={disabled || busy}
                  >
                    <SelectTrigger id={`${status.client_id}-final-type`}>
                      <SelectValue placeholder={t("appointmentStatusesFinalTypePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {FINAL_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {t(`appointmentStatusesFinalTypeOption.${option}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor={`${status.client_id}-color`}>{t("appointmentStatusesColorLabel")}</Label>
                  <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                    <input
                      id={`${status.client_id}-color`}
                      type="color"
                      value={status.color}
                      disabled={disabled || busy}
                      onChange={(event) => onChange(status.client_id, { color: event.target.value.toLowerCase() })}
                      className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                    />
                    <Input
                      value={status.color}
                      disabled={disabled || busy}
                      onChange={(event) => onChange(status.client_id, { color: event.target.value.toLowerCase() })}
                      className="text-sm"
                      inputSize="sm"
                      style={{ border: 0, padding: 0, background: "transparent" }}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor={`${status.client_id}-sort-order`}>{t("appointmentStatusesOrderLabel")}</Label>
                  <Input
                    id={`${status.client_id}-sort-order`}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    value={status.sort_order}
                    disabled={disabled || busy}
                    onChange={(event) => {
                      const nextValue = Number.parseInt(event.target.value, 10);
                      onChange(status.client_id, {
                        sort_order: Number.isNaN(nextValue) ? status.sort_order : nextValue,
                      });
                    }}
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-[160px_minmax(0,1fr)]">
                <div className="space-y-1">
                  <Label htmlFor={`${status.client_id}-active`}>{t("appointmentStatusesVisibleLabel")}</Label>
                  <div className="flex h-10 items-center rounded-md border px-3">
                    <Switch
                      id={`${status.client_id}-active`}
                      checked={status.is_active}
                      disabled={disabled || busy}
                      onCheckedChange={(checked) => {
                        onChange(status.client_id, {
                          is_active: checked,
                          replacement_name:
                            checked || activeReplacementOptions.length === 0
                              ? null
                              : status.replacement_name ?? activeReplacementOptions[0]?.name ?? null,
                        });
                      }}
                    />
                  </div>
                </div>

                {!status.is_active ? (
                  <div className="space-y-1">
                    <Label htmlFor={`${status.client_id}-replacement`}>{t("appointmentStatusesReplacementLabel")}</Label>
                    <Select
                      value={status.replacement_name ?? ""}
                      onValueChange={(value) => onChange(status.client_id, { replacement_name: value })}
                      disabled={disabled || busy || activeReplacementOptions.length === 0}
                    >
                      <SelectTrigger id={`${status.client_id}-replacement`}>
                        <SelectValue placeholder={t("appointmentStatusesReplacementPlaceholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {activeReplacementOptions.map((option) => (
                          <SelectItem key={option.client_id} value={option.name ?? option.client_id}>
                            <span className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: option.color }} />
                              <span>{option.display_name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t("appointmentStatusesReplacementDescription")}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" disabled={disabled || busy} onClick={onAdd}>
          <PlusCircle className="mr-2 h-4 w-4" />
          {t("appointmentStatusesAdd")}
        </Button>
        <Button type="button" disabled={disabled || busy || statuses.length === 0} onClick={onSave}>
          {busy ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("saving")}
            </>
          ) : (
            t("appointmentStatusesSave")
          )}
        </Button>
      </div>
    </div>
  );
}

export function ClinicAppointmentStatusSettings() {
  const { t } = useTranslation("settings");
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;
  const { data: appointmentStatusesData, isLoading } = useAppointmentStatuses(
    "appointment",
    { includeInactive: true },
  );
  const updateStatuses = useUpdateClinicAppointmentStatuses();
  const appointmentStatuses = useMemo(() => appointmentStatusesData ?? [], [appointmentStatusesData]);

  const [draftStatuses, setDraftStatuses] = useState<DraftStatus[]>([]);

  useEffect(() => {
    setDraftStatuses(mapStatusesToDraft(appointmentStatuses));
  }, [appointmentStatuses]);
  const disabled = !clinicId;

  const updateDraft = (
    setter: Dispatch<SetStateAction<DraftStatus[]>>,
    clientId: string,
    next: Partial<DraftStatus>,
  ) => {
    setter((current) =>
      current.map((status) => (status.client_id === clientId ? { ...status, ...next } : status)),
    );
  };

  const addDraftStatus = (
    setter: Dispatch<SetStateAction<DraftStatus[]>>,
    current: DraftStatus[],
  ) => {
    const nextSortOrder = (current.at(-1)?.sort_order ?? 0) + 10;

    setter((existing) => [
      ...existing,
      {
        client_id: createDraftId(),
        name: createDraftStatusName(),
        display_name: "",
        is_active: true,
        was_active: true,
        sort_order: nextSortOrder,
        color: getStatusColor("agendado"),
        final_type: "normal",
        replacement_name: null,
        is_system: false,
        is_new: true,
      },
    ]);
  };

  const removeDraftStatus = (
    setter: Dispatch<SetStateAction<DraftStatus[]>>,
    clientId: string,
  ) => {
    setter((current) => current.filter((status) => status.client_id !== clientId));
  };

  const validateDraftStatuses = (statuses: DraftStatus[]) => {
    if (statuses.some((status) => status.display_name.trim() === "")) {
      toast.error(t("appointmentStatusesDisplayNameRequired"));
      return false;
    }

    if (statuses.some((status) => !/^#[0-9a-f]{6}$/i.test(status.color))) {
      toast.error(t("appointmentStatusesColorInvalid"));
      return false;
    }

    if (
      statuses.some(
        (status) =>
          status.was_active &&
          !status.is_active &&
          !status.replacement_name &&
          !status.is_new,
      )
    ) {
      toast.error(t("appointmentStatusesReplacementRequired"));
      return false;
    }

    return true;
  };

  const saveStatuses = async (statuses: DraftStatus[]) => {
    if (!validateDraftStatuses(statuses)) return;

    try {
      await updateStatuses.mutateAsync({
        scope: "appointment",
        statuses: statuses.map((status, index) => {
          const resolvedReplacement = status.is_active
            ? null
            : statuses.find(
                (candidate) =>
                  candidate.name === status.replacement_name ||
                  candidate.client_id === status.replacement_name,
              )?.name ?? null;

          return {
            name: status.name,
            display_name: status.display_name.trim(),
            description: null,
            is_active: status.is_active,
            sort_order: Number.isFinite(status.sort_order) ? status.sort_order : (index + 1) * 10,
            final_type: status.final_type,
            color: status.color.toLowerCase(),
            is_system: status.is_system,
            replacement_name: resolvedReplacement,
          };
        }),
      });
      toast.success(t("appointmentStatusesSaved"));
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message === "clinic_required"
          ? t("generalClinicRequired")
          : t("appointmentStatusesSaveError");
      toast.error(message);
    }
  };

  if (isLoading && draftStatuses.length === 0) {
    return (
      <div className="flex min-h-[160px] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">{t("appointmentStatusesDescription")}</p>
        <p className="text-xs text-muted-foreground">
          {clinicId ? t("generalClinicScoped") : t("generalClinicRequired")}
        </p>
      </div>
      <StatusScopeSection
        title={t("appointmentStatusesAppointmentTitle")}
        description={t("appointmentStatusesAppointmentDescription")}
        statuses={draftStatuses}
        busy={updateStatuses.isPending}
        disabled={disabled}
        onChange={(clientId, next) => updateDraft(setDraftStatuses, clientId, next)}
        onAdd={() => addDraftStatus(setDraftStatuses, draftStatuses)}
        onRemove={(clientId) => removeDraftStatus(setDraftStatuses, clientId)}
        onSave={() => {
          void saveStatuses(draftStatuses);
        }}
      />
    </div>
  );
}
